import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Phone, MessageCircle, Check, MapPin, Clock, Users, Car, FileText, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import { COMPANY_INFO } from '../mock';
import { InternalLinksSection } from '../components/InternalLinksSection';

const UnfallgutachtenNorderstedtPage = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${API_URL}/api/leads/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...formData, source: 'unfallgutachten-norderstedt-landing-page' })
    });
    alert('Vielen Dank! Wir melden uns schnellstmöglich.');
    setFormData({ name: '', phone: '', message: '' });
  };

  const faqs = [
    {
      q: "Wie schnell bekomme ich einen Termin in Norderstedt?",
      a: "Als Ihr direkter Nachbar aus Rellingen sind wir in Norderstedt besonders schnell vor Ort – oft noch am selben Tag. Die Fahrzeit von unserem Hauptsitz nach Norderstedt-Mitte beträgt nur etwa 15 Minuten. Bei dringenden Fällen, etwa wenn Ihr Fahrzeug nicht fahrbereit ist, können wir meist innerhalb weniger Stunden bei Ihnen sein."
    },
    {
      q: "Wer trägt die Kosten für das Unfallgutachten?",
      a: "Bei einem unverschuldeten Unfall übernimmt die gegnerische Haftpflichtversicherung die Kosten für das Gutachten vollständig. Sie haben das gesetzliche Recht auf freie Gutachterwahl – lassen Sie sich nicht von der Versicherung zu einem eigenen Gutachter drängen. Bei einem verschuldeten Unfall oder Kaskoschaden beraten wir Sie gerne über Ihre Optionen."
    },
    {
      q: "Kommt ihr in alle Norderstedter Stadtteile?",
      a: "Ja, wir kommen in ganz Norderstedt zu Ihnen: Garstedt, Harksheide, Friedrichsgabe, Glashütte und alle anderen Stadtteile. Auch die angrenzenden Gemeinden wie Quickborn, Henstedt-Ulzburg, Tangstedt, Ellerau und Hamburg-Nord gehören zu unserem Kerngebiet. Wir kommen dorthin, wo Ihr Fahrzeug steht – ob zu Hause, bei der Arbeit oder in der Werkstatt."
    },
    {
      q: "Was passiert bei einem Unfall auf der A7 bei Norderstedt?",
      a: "Unfälle auf der A7 zwischen dem Dreieck Hamburg-Nordwest und Quickborn sind für uns Routine. Wir können direkt zur Unfallstelle kommen, oder – was häufiger vorkommt – zum Abschleppdienst oder zur Werkstatt, wo Ihr Fahrzeug nach dem Unfall abgestellt wurde. Wichtig: Dokumentieren Sie die Unfallstelle mit Fotos, bevor die Fahrzeuge bewegt werden."
    },
    {
      q: "Warum sollte ich nicht den Gutachter der Versicherung nehmen?",
      a: "Der Gutachter der gegnerischen Versicherung wird von dieser bezahlt und hat naturgemäß das Interesse, den Schaden möglichst niedrig anzusetzen. Ein unabhängiger Gutachter wie wir arbeitet ausschließlich in Ihrem Interesse. Studien zeigen, dass unabhängige Gutachten im Durchschnitt 15-20% höhere Schadensummen ermitteln als Versicherungsgutachter."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Unfallgutachten Norderstedt | Kfz-Sachverständige vor Ort | Dein Gutachten Direkt</title>
        <meta name="description" content="Unfallgutachten in Norderstedt – Ihr Gutachter aus der Nachbarschaft. Nur 15 Min. von Rellingen. Gerichtsfeste Gutachten, schnelle Termine. Kostenlos bei unverschuldetem Unfall!" />
        <meta name="keywords" content="Unfallgutachten Norderstedt, Kfz Gutachter Norderstedt, Sachverständiger Norderstedt, Gutachter Garstedt, Gutachter Harksheide, Unfallgutachter Hamburg-Nord" />
        <link rel="canonical" href="https://www.dein-gutachten-direkt.de/unfallgutachten-norderstedt" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Dein Gutachten Direkt - Norderstedt",
            "description": "Kfz-Sachverständigenbüro mit über 10 Jahren Erfahrung. Unabhängige Unfallgutachten in Norderstedt und Umgebung. Nur 15 Minuten von unserem Hauptsitz in Rellingen entfernt.",
            "telephone": "+4941018471160",
            "email": "info@dein-gutachten-direkt.de",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Norderstedt",
              "addressRegion": "Schleswig-Holstein",
              "addressCountry": "DE"
            },
            "areaServed": [
              {"@type": "City", "name": "Norderstedt"},
              {"@type": "City", "name": "Garstedt"},
              {"@type": "City", "name": "Harksheide"},
              {"@type": "City", "name": "Quickborn"},
              {"@type": "City", "name": "Henstedt-Ulzburg"},
              {"@type": "City", "name": "Hamburg-Nord"}
            ],
            "priceRange": "€€"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          })}
        </script>
      </Helmet>

      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 pt-28 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green/20 border border-brand-green/30 rounded-full mb-6">
                  <MapPin className="w-4 h-4 text-brand-green" />
                  <span className="text-brand-green font-semibold text-sm">Norderstedt & Hamburg-Nord</span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Unfallgutachten
                  <br />
                  <span className="text-brand-green">in Norderstedt</span>
                </h1>
                
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Ihr Kfz-Gutachter aus der Nachbarschaft: Von unserem Hauptsitz in <strong className="text-white">Rellingen</strong> sind wir in nur 
                  <strong className="text-white"> 15 Minuten</strong> in Norderstedt – für schnelle, gerichtsfeste Gutachten direkt vor Ort.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Nur 15 Min. entfernt</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Über 10 Jahre Erfahrung</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Gerichtsfeste Gutachten</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Zusammenarbeit mit Anwälten</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Alle Stadtteile</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Termin oft am selben Tag</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a href={`tel:${COMPANY_INFO.phone}`}>
                    <Button size="lg" className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-dark text-white text-lg px-8 py-6 rounded-xl font-bold">
                      <Phone className="w-5 h-5 mr-2" />
                      Jetzt anrufen
                    </Button>
                  </a>
                  <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 rounded-xl font-bold">
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                </div>

                <p className="text-gray-400 text-sm mt-6">
                  Bei unverschuldetem Unfall: Kostenübernahme durch gegnerische Versicherung
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Ihr Vorteil: Wir sind Nachbarn</h2>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Kurze Wege</p>
                      <p className="text-gray-400">Hauptsitz Rellingen → Norderstedt: 15 Min.</p>
                      <p className="text-gray-400">Alle Stadtteile schnell erreichbar</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Schnelle Termine</p>
                      <p className="text-gray-400">In Norderstedt oft am selben Tag</p>
                      <p className="text-gray-400">Notfälle innerhalb weniger Stunden</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Users className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Lokale Expertise</p>
                      <p className="text-gray-400">Wir kennen die Region bestens</p>
                      <p className="text-gray-400">Netzwerk mit lokalen Werkstätten & Anwälten</p>
                    </div>
                  </div>
                </div>

                <div className="bg-brand-green/20 border border-brand-green/30 rounded-xl p-4">
                  <p className="text-brand-green font-semibold text-sm">
                    <strong>Freie Gutachterwahl:</strong> Sie haben das Recht, Ihren Gutachter selbst zu wählen. Die Versicherung muss die Kosten bei unverschuldetem Unfall übernehmen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Über uns Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">Über uns</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Ihr Kfz-Gutachter für Norderstedt</h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed text-lg">
                <strong>Dein Gutachten Direkt</strong> ist Ihr lokaler Kfz-Sachverständiger für Norderstedt und die nördliche Metropolregion Hamburg. 
                Mit unserem Hauptsitz in <strong>Rellingen</strong> – direkt an der Grenze zu Norderstedt – sind wir Ihr Nachbar und können besonders schnell bei Ihnen sein.
              </p>

              <p className="text-gray-700 leading-relaxed text-lg">
                Seit über <strong>10 Jahren</strong> erstellen wir unabhängige Unfallgutachten für Geschädigte in Norderstedt. 
                Ob Auffahrunfall auf der Ulzburger Straße, Parkrempler im Herold-Center oder Wildunfall auf dem Weg nach Henstedt-Ulzburg – 
                wir dokumentieren jeden Schaden professionell und <strong>gerichtsfest</strong>.
              </p>

              <p className="text-gray-700 leading-relaxed text-lg">
                Als Teil der lokalen Gemeinschaft kennen wir Norderstedt bestens. Wir wissen, welche Werkstätten zuverlässig arbeiten, 
                und haben gute Kontakte zu <strong>Fachanwälten für Verkehrsrecht</strong> in der Region.
              </p>

              <div className="bg-gray-50 rounded-2xl p-8 my-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Das zeichnet uns aus:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Lokal:</strong> Nur 15 Minuten von Norderstedt entfernt – Ihr Gutachter aus der Nachbarschaft</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Unabhängig:</strong> Wir arbeiten ausschließlich für Sie – nicht für Versicherungen</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Erfahren:</strong> Über 10 Jahre Erfahrung in der Kfz-Schadensbewertung</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Gerichtsfest:</strong> Unsere Gutachten sind rechtssicher und vor Gericht verwendbar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Schnell:</strong> In Norderstedt oft am selben Tag, bei Notfällen innerhalb weniger Stunden</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Was beinhaltet unser Gutachten */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">Leistungen</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Was beinhaltet unser Gutachten?</h2>
              <p className="text-xl text-gray-600 mt-4">Ein professionelles Unfallgutachten sichert Ihnen alle Ansprüche</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Reparaturkostenkalkulation</h3>
                </div>
                <p className="text-gray-700">Detaillierte Berechnung aller Reparaturkosten nach Herstellervorgaben. Mit Arbeitslohn, Original-Ersatzteilen, Lackierung und allen Nebenkosten.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Car className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Wertminderung</h3>
                </div>
                <p className="text-gray-700">Der merkantile Wertverlust nach dem Unfall – auch bei perfekter Reparatur. Bei neueren Fahrzeugen oft mehrere hundert Euro zusätzlich.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Nutzungsausfall</h3>
                </div>
                <p className="text-gray-700">Entschädigung für jeden Tag ohne Ihr Fahrzeug. Bei einem Mittelklassewagen ca. 35-50€ pro Tag – das summiert sich schnell.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Wiederbeschaffungswert</h3>
                </div>
                <p className="text-gray-700">Bei Totalschaden: Was kostet ein gleichwertiges Fahrzeug? Wir ermitteln den fairen Marktwert anhand aktueller lokaler Angebote.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Restwertermittlung</h3>
                </div>
                <p className="text-gray-700">Der Verkaufswert Ihres beschädigten Fahrzeugs. Wichtig für die korrekte Totalschadenabrechnung.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Fotodokumentation</h3>
                </div>
                <p className="text-gray-700">30-50 professionelle Fotos aller Schäden aus verschiedenen Winkeln. Rechtssichere Beweissicherung für den Ernstfall.</p>
              </div>
            </div>

            <div className="bg-brand-green/10 border-2 border-brand-green/30 rounded-2xl p-6 mt-8 text-center">
              <p className="text-gray-900 font-semibold">
                <strong>Wichtig:</strong> Ein Werkstatt-Kostenvoranschlag enthält nur Reparaturkosten. 
                Wertminderung und Nutzungsausfall fehlen – das kann Sie bei einem Mittelklassewagen 500-1.500€ kosten!
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Häufige Fragen zu Unfallgutachten in Norderstedt</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-3 text-lg">{faq.q}</h3>
                  <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stadtteile */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Norderstedt & Umgebung</h2>
            <p className="text-lg text-gray-600 mb-8">Alle Norderstedter Stadtteile und angrenzende Gemeinden im Überblick</p>
            <div className="flex flex-wrap justify-center gap-3">
              {['Norderstedt-Mitte', 'Garstedt', 'Harksheide', 'Friedrichsgabe', 'Glashütte', 'Quickborn', 'Henstedt-Ulzburg', 'Kaltenkirchen', 'Tangstedt', 'Ellerau', 'Alveslohe', 'Hamburg-Langenhorn', 'Hamburg-Fuhlsbüttel', 'Hasloh', 'Bönningstedt'].map((ort) => (
                <span key={ort} className="px-4 py-2 bg-white rounded-lg border border-gray-200 text-gray-700 font-medium hover:border-brand-green/50 transition-colors">
                  {ort}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-gray-900 to-black">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Jetzt Gutachten in Norderstedt beauftragen</h2>
            <p className="text-xl text-gray-300 mb-8">Ihr Nachbar aus Rellingen • Termin oft am selben Tag</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${COMPANY_INFO.phone}`}>
                <Button size="lg" className="bg-brand-green hover:bg-brand-green-dark text-white text-lg px-10 py-6 rounded-xl font-bold">
                  <Phone className="w-5 h-5 mr-2" />
                  04101 847 11 60
                </Button>
              </a>
              <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-6 rounded-xl font-bold">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>

        <InternalLinksSection currentPage="/unfallgutachten-norderstedt" />
      </main>

      <Footer />
    </div>
  );
};

export default UnfallgutachtenNorderstedtPage;
